import type { CollectionInputs } from '@ayumia/component-runtime';
import type { Filters } from './FilterBar';

/**
 * État d'une liste (ADR-094, ADR-096, ADR-099) : recherche, filtre rapide, période, filtres par champ, tri et
 * représentation affichée (identifiant d'une représentation de la vue : table, cartes, Kanban…). Les pages chargées ne s'y
 * écrivent plus : le défilement infini les recharge (un ancien `page=N` est ignoré). Il se sérialise dans l'URL (après `?` dans le fragment) pour survivre au
 * rechargement, au retour arrière et au lien partagé. Il ne porte que la forme gouvernée de l'API
 * (triplets champ, opérateur, valeur) : le serveur revalide tout, aucune requête Mongo n'y entre.
 */

/** Entrées du conteneur Collection qui portent la requête (filtres rapides, période). */
export type ListBody = CollectionInputs;
export type PeriodPreset = NonNullable<ListBody['period']>['presets'][number];
export type SortState = { field: string; direction: 'asc' | 'desc' };
/** Représentation choisie par le sélecteur de vue (ADR-096 §3) : l'identifiant d'une représentation de la vue (ADR-099). */
export type RepresentationId = string;

export interface ListState {
  q: string;
  /** Filtre rapide choisi ; `undefined` : celui par défaut de la vue. */
  quick?: string;
  /** Préréglage de période choisi ; `undefined` : celui par défaut de la vue. */
  period?: PeriodPreset;
  filters: Filters;
  /** Tri choisi ; `undefined` : le tri par défaut de la vue. */
  sort?: SortState;
  /** Représentation choisie ; `undefined` : celle par défaut de la vue. */
  view?: RepresentationId;
}

export const EMPTY_LIST_STATE: ListState = { q: '', filters: {} };

const FIELD = /^[a-z][a-z0-9_]*$/;
const OPERATORS = new Set(['eq', 'ne', 'in', 'gt', 'gte', 'lt', 'lte', 'contains', 'starts_with', 'exists']);
const PRESETS: ReadonlySet<string> = new Set(['all', 'today', 'yesterday', 'last_7_days', 'last_30_days', 'this_week', 'this_month', 'last_month', 'this_quarter', 'this_year', 'last_year']);

/** Lit l'état depuis la chaîne de l'URL ; une entrée mal formée est ignorée, jamais devinée. */
export function parseListState(serialized: string | undefined): ListState {
  const state: ListState = { q: '', filters: {} };
  if (serialized === undefined || serialized === '') return state;
  const params = new URLSearchParams(serialized);
  for (const [key, value] of params) {
    if (key === 'q') state.q = value.slice(0, 100);
    else if (key === 'tab' && FIELD.test(value)) state.quick = value;
    else if (key === 'period' && PRESETS.has(value)) state.period = value as PeriodPreset;
    else if (key === 'sort') {
      const field = value.replace(/^-/, '');
      if (FIELD.test(field)) state.sort = { field, direction: value.startsWith('-') ? 'desc' : 'asc' };
    } else if (key === 'view' && FIELD.test(value)) {
      state.view = value;
    } else if (key.startsWith('f.')) {
      const [, field, operator] = key.split('.');
      if (field !== undefined && operator !== undefined && FIELD.test(field) && OPERATORS.has(operator) && value !== '') state.filters[field] = { ...(state.filters[field] ?? {}), [operator]: value };
    }
  }
  return state;
}

/** Sérialise l'état ; seules les valeurs différentes du défaut sont écrites. */
export function formatListState(state: ListState): string {
  const params = new URLSearchParams();
  if (state.q !== '') params.set('q', state.q);
  if (state.quick !== undefined) params.set('tab', state.quick);
  if (state.period !== undefined) params.set('period', state.period);
  for (const [field, operators] of Object.entries(state.filters)) {
    for (const [operator, value] of Object.entries(operators)) if (value !== '') params.append(`f.${field}.${operator}`, value);
  }
  if (state.sort !== undefined) params.set('sort', `${state.sort.direction === 'desc' ? '-' : ''}${state.sort.field}`);
  if (state.view !== undefined) params.set('view', state.view);
  return params.toString();
}

/** Filtre rapide effectif : celui choisi s'il existe, sinon le défaut déclaré, sinon le premier. */
export function activeQuickFilter(list: ListBody, state: ListState): NonNullable<ListBody['quick_filters']>['items'][number] | undefined {
  const items = list.quick_filters?.items ?? [];
  return items.find((item) => item.quick_filter_id === state.quick) ?? items.find((item) => item.default === true) ?? items[0];
}

export function activePeriod(list: ListBody, state: ListState): PeriodPreset | undefined {
  if (list.period === undefined) return undefined;
  const chosen = state.period ?? list.period.default_preset ?? 'all';
  return list.period.presets.includes(chosen) || chosen === 'all' ? chosen : 'all';
}

const pad = (value: number): string => String(value).padStart(2, '0');
/** Date civile AAAA-MM-JJ d'une date locale (ADR-085). */
export function civil(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Bornes d'un préréglage en dates civiles du navigateur, fin exclue (ADR-085). Le fuseau du
 * navigateur tient lieu de fuseau du site ; la conversion au fuseau du site par le serveur est P1.
 */
export function periodRange(preset: PeriodPreset, now: Date = new Date()): { from: Date; to: Date } | null {
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const day = (offset: number): Date => new Date(y, m, d + offset);
  switch (preset) {
    case 'all':
      return null;
    case 'today':
      return { from: day(0), to: day(1) };
    case 'yesterday':
      return { from: day(-1), to: day(0) };
    case 'last_7_days':
      return { from: day(-6), to: day(1) };
    case 'last_30_days':
      return { from: day(-29), to: day(1) };
    case 'this_week': {
      const monday = (now.getDay() + 6) % 7;
      return { from: day(-monday), to: day(7 - monday) };
    }
    case 'this_month':
      return { from: new Date(y, m, 1), to: new Date(y, m + 1, 1) };
    case 'last_month':
      return { from: new Date(y, m - 1, 1), to: new Date(y, m, 1) };
    case 'this_quarter': {
      const first = m - (m % 3);
      return { from: new Date(y, first, 1), to: new Date(y, first + 3, 1) };
    }
    case 'this_year':
      return { from: new Date(y, 0, 1), to: new Date(y + 1, 0, 1) };
    case 'last_year':
      return { from: new Date(y - 1, 0, 1), to: new Date(y, 0, 1) };
  }
}

export const PRESET_LABELS: Readonly<Record<PeriodPreset, string>> = {
  all: 'Toutes les dates',
  today: 'Aujourd’hui',
  yesterday: 'Hier',
  last_7_days: '7 derniers jours',
  last_30_days: '30 derniers jours',
  this_week: 'Cette semaine',
  this_month: 'Ce mois-ci',
  last_month: 'Le mois dernier',
  this_quarter: 'Ce trimestre',
  this_year: 'Cette année',
  last_year: 'L’année dernière',
};

/** Ajoute des filtres à d'autres ; le dernier l'emporte sur un même champ et un même opérateur. */
export function mergeFilters(...parts: ReadonlyArray<Readonly<Record<string, Readonly<Record<string, string | readonly string[]>>>> | undefined>): Filters {
  const out: Filters = {};
  for (const part of parts) {
    for (const [field, operators] of Object.entries(part ?? {})) {
      for (const [operator, value] of Object.entries(operators)) {
        const flat = Array.isArray(value) ? value.join(',') : (value as string);
        if (flat !== '') out[field] = { ...(out[field] ?? {}), [operator]: flat };
      }
    }
  }
  return out;
}

/**
 * Filtre de période pour le champ de la vue : une date civile pour un champ `date`, l'instant de
 * minuit local pour un `datetime`.
 */
export function periodFilter(list: ListBody, preset: PeriodPreset | undefined, fieldType: string | undefined, now?: Date): Filters {
  if (list.period === undefined || preset === undefined) return {};
  const range = periodRange(preset, now);
  if (range === null) return {};
  const format = (date: Date): string => (fieldType === 'date' ? civil(date) : date.toISOString());
  return { [list.period.field]: { gte: format(range.from), lt: format(range.to) } };
}

/** Vrai quand l'utilisateur a restreint la liste (recherche, filtre, période ou onglet autre que le défaut). */
export function isNarrowed(list: ListBody, state: ListState): boolean {
  const quick = activeQuickFilter(list, state);
  const defaultQuick = activeQuickFilter(list, { q: state.q, filters: state.filters });
  return state.q !== '' || Object.keys(state.filters).length > 0 || (state.period !== undefined && state.period !== (list.period?.default_preset ?? 'all')) || (quick !== undefined && quick !== defaultQuick && Object.keys(quick.filter).length > 0);
}
